/*
 * AdminDropship — single page covering the entire dropship operational layer.
 * Sprint 1 + Sprint 4 + iter-87 visibility audit.
 *
 *   • Orders         — cross-tenant orders console (iter-87 · P0)
 *   • Invoices       — cross-tenant invoices roster (iter-87 · P0)
 *   • Products       — cross-seller dropship-enabled catalog (iter-87 · P0)
 *   • Tracking       — cross-order tracking-events audit (iter-87 · P1)
 *   • SLA            — cross-order SLA-extensions audit (iter-87 · P1)
 *   • Analytics      — platform-wide dropship analytics (iter-87 · P2)
 *   • Claims         — moderate disputes, view evidence photos, force-resolve
 *   • Returns        — admin force-verify / force-reject / void
 *   • Sync Rules     — list every catalog automation rule, force-run
 *   • Shopify Stores — every connected store, admin disconnect
 *   • Policies       — bilateral payment + shipping policies
 *   • CSV Batches    — upload audit trail
 *   • Onboarding     — per-buyer completion %
 */
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, RotateCcw, Filter, Plug, Receipt, FileText,
  Sparkles, Store as StoreIcon, ChevronRight, Loader2, X, Image as ImageIcon,
  ShieldCheck, ShieldOff, Ban, Check, Clock, RefreshCw, Trash2, Search,
  Package, Truck, ClipboardList, BarChart3, ShoppingCart, Send, Download, Mail,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "../../components/PortalShell";

const TABS = [
  { k: "orders",      l: "Orders",      icon: ShoppingCart },
  { k: "invoices",    l: "Invoices",    icon: Receipt },
  { k: "products",    l: "Products",    icon: Package },
  { k: "tracking",    l: "Tracking",    icon: Truck },
  { k: "sla",         l: "SLA",         icon: ClipboardList },
  { k: "analytics",   l: "Analytics",   icon: BarChart3 },
  { k: "claims",      l: "Claims",      icon: AlertTriangle },
  { k: "returns",     l: "Returns",     icon: RotateCcw },
  { k: "rules",       l: "Sync rules",  icon: Sparkles },
  { k: "stores",      l: "Shopify",     icon: StoreIcon },
  { k: "policies",    l: "Policies",    icon: FileText },
  { k: "csv",         l: "CSV history", icon: Receipt },
  { k: "onboarding",  l: "Onboarding",  icon: Plug },
];

export default function AdminDropship() {
  const [tab, setTab] = useState("orders");
  return (
    <div>
      <PageHeader title="Dropship operations"
        subtitle="Platform backbone for every dropship action — orders · invoices · products · tracking · SLA · analytics · claims · returns · rules · Shopify · policies · uploads · onboarding."/>
      <div className="mt-6 flex items-center gap-1 border-b border-border overflow-x-auto">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`admin-dropship-tab-${t.k}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 transition-colors ${
              tab === t.k ? "border-foreground text-foreground" : "border-transparent text-foreground/55 hover:text-foreground"
            }`}>
            <t.icon className="w-3.5 h-3.5"/>{t.l}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "orders"     && <OrdersTab/>}
        {tab === "invoices"   && <InvoicesTab/>}
        {tab === "products"   && <ProductsTab/>}
        {tab === "tracking"   && <TrackingTab/>}
        {tab === "sla"        && <SlaTab/>}
        {tab === "analytics"  && <AnalyticsTab/>}
        {tab === "claims"     && <ClaimsTab/>}
        {tab === "returns"    && <ReturnsTab/>}
        {tab === "rules"      && <SyncRulesTab/>}
        {tab === "stores"     && <ShopifyTab/>}
        {tab === "policies"   && <PoliciesTab/>}
        {tab === "csv"        && <CsvBatchesTab/>}
        {tab === "onboarding" && <OnboardingTab/>}
      </div>
    </div>
  );
}

/* ─────────────── Reusable building blocks ─────────────── */
function Loading() {
  return <div className="rounded-2xl border border-border bg-white p-12 text-center text-foreground/45">
    <Loader2 className="w-5 h-5 animate-spin mx-auto"/>
  </div>;
}
function Empty({ what }) {
  return <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-10 text-center text-foreground/55 text-[13px]">
    No {what} yet.
  </div>;
}
function Pill({ tone = "neutral", children }) {
  const map = {
    neutral: "bg-foreground/8 text-foreground/65 border-foreground/10",
    warn:    "bg-amber-100 text-amber-900 border-amber-300",
    rose:    "bg-rose-100 text-rose-800 border-rose-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    blue:    "bg-blue-100 text-blue-800 border-blue-200",
    indigo:  "bg-indigo-100 text-indigo-800 border-indigo-200",
  };
  return <span className={`inline-flex items-center gap-1 text-[10px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${map[tone] || map.neutral}`}>{children}</span>;
}
function statusTone(s) {
  if (["resolved", "verified", "applied", "paid"].includes(s)) return "emerald";
  if (["pending", "filed", "under_review"].includes(s)) return "warn";
  if (["contested", "rejected", "overdue", "void", "reversed", "disconnected"].includes(s)) return "rose";
  return "neutral";
}
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso.slice(0, 19).replace("T", " "); }
}

/* ─────────────── 1 · Claims ─────────────── */
function ClaimsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusChip, setStatusChip] = useState("all");
  const [drawer, setDrawer] = useState(null);
  const load = () => { setLoading(true);
    api.get("/admin/dropship/claims?limit=500")
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => {
    let xs = rows;
    if (statusChip !== "all") xs = xs.filter(r => r.status === statusChip);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(r =>
        (r.order_code || "").toLowerCase().includes(q) ||
        (r.kind || "").toLowerCase().includes(q) ||
        (r.buyer?.company || r.buyer?.name || "").toLowerCase().includes(q) ||
        (r.seller?.company || r.seller?.name || "").toLowerCase().includes(q),
      );
    }
    return xs;
  }, [rows, statusChip, search]);
  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, contested: 0, resolved: 0, applied: 0, void: 0, reversed: 0 };
    rows.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { v: "all",       l: "All" },
          { v: "pending",   l: "Pending" },
          { v: "contested", l: "Contested" },
          { v: "applied",   l: "Applied" },
          { v: "resolved",  l: "Resolved" },
          { v: "void",      l: "Void" },
        ].map(o => (
          <button key={o.v} onClick={() => setStatusChip(o.v)}
            data-testid={`admin-claims-chip-${o.v}`}
            className={`rounded-full h-8 px-3 text-[12px] mono ${
              statusChip === o.v ? "bg-foreground text-background" : "bg-white border border-border hover:bg-secondary"
            }`}>
            {o.l} <span className="opacity-60">({counts[o.v] || 0})</span>
          </button>
        ))}
        <div className="flex-1 relative min-w-[240px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by order, kind, buyer, seller…"
            className="pl-9 h-9"/>
        </div>
      </div>
      {loading ? <Loading/> : filtered.length === 0 ? <Empty what="claims"/> : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <div className="grid grid-cols-[100px_120px_1fr_1fr_90px_100px_36px] gap-3 px-4 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
            <div>Order</div><div>Kind</div><div>Buyer</div><div>Seller</div>
            <div className="text-right">Deduct</div><div>Status</div><div></div>
          </div>
          <div className="divide-y divide-border max-h-[640px] overflow-y-auto">
            {filtered.map(r => (
              <button key={r.id} onClick={() => setDrawer(r)}
                data-testid={`admin-claim-row-${r.id}`}
                className="w-full grid grid-cols-[100px_120px_1fr_1fr_90px_100px_36px] gap-3 px-4 py-2.5 items-center text-[13px] hover:bg-secondary/30 text-left">
                <div className="mono text-[12px]">{r.order_code}</div>
                <div className="capitalize">{(r.kind || "").replace("_", " ")}</div>
                <div className="truncate">{r.buyer?.company || r.buyer?.name || r.buyer_id?.slice(0, 8)}</div>
                <div className="truncate">{r.seller?.company || r.seller?.name || r.seller_id?.slice(0, 8)}</div>
                <div className="text-right font-medium">{money(r.deduction)}</div>
                <div><Pill tone={statusTone(r.status)}>{r.status}</Pill>
                  {r.evidence_photo_count > 0 && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-[9.5px] text-foreground/55"><ImageIcon className="w-2.5 h-2.5"/>{r.evidence_photo_count}</span>
                  )}
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-foreground/40"/>
              </button>
            ))}
          </div>
        </div>
      )}
      <ClaimDrawer claim={drawer} onClose={() => setDrawer(null)} onActioned={load}/>
    </div>
  );
}

function ClaimDrawer({ claim, onClose, onActioned }) {
  const [full, setFull] = useState(null);
  const [action, setAction] = useState(null);  // "force_resolve" | "force_reverse" | "void"
  const [override, setOverride] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (!claim?.id) return;
    api.get(`/admin/dropship/claims/${claim.id}`)
      .then(r => setFull(r.data))
      .catch(() => {});
  }, [claim?.id]);
  if (!claim) return null;
  const c = full || claim;
  const submit = async () => {
    if (!action) return;
    setSubmitting(true);
    try {
      const body = { action, note: note || undefined };
      if (action === "force_resolve" && override) body.deduction_override = Number(override);
      await api.patch(`/admin/dropship/claims/${claim.id}`, body);
      toast.success(`Claim ${action.replace("_", " ")}`);
      onActioned?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Override failed");
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={!!claim} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Claim · {c.order_code}</DialogTitle>
          <DialogDescription>Bilateral claim between buyer and seller — admin can override outcome.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-[13px]">
          <div className="grid grid-cols-2 gap-3">
            <Field l="Buyer"  v={c.buyer?.company || c.buyer?.name}/>
            <Field l="Seller" v={c.seller?.company || c.seller?.name}/>
            <Field l="Kind"   v={(c.kind || "").replace("_", " ")}/>
            <Field l="Status" v={c.status}/>
            <Field l="Deduction"     v={money(c.deduction)}/>
            <Field l="Order value"   v={money(c.order_value)}/>
            <Field l="Filed"  v={formatTs(c.filed_at)}/>
            {c.admin_action_at && <Field l="Last admin action" v={`${c.admin_action} · ${formatTs(c.admin_action_at)}`}/>}
          </div>
          <div className="rounded-xl border border-border bg-secondary/20 p-3">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-1">Description</div>
            <div className="text-[12.5px]">{c.description}</div>
          </div>
          {c.evidence_photos?.length > 0 && (
            <div className="rounded-xl border border-border bg-white p-3">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-2 inline-flex items-center gap-1">
                <ImageIcon className="w-3 h-3"/>Evidence ({c.evidence_photos.length})
              </div>
              <div className="grid grid-cols-3 gap-2">
                {c.evidence_photos.map((p, i) => (
                  <a key={i} href={p.data_url} target="_blank" rel="noreferrer"
                    className="aspect-square rounded-lg border border-border overflow-hidden bg-secondary/20">
                    {p.data_url
                      ? <img src={p.data_url} alt={`Ev ${i+1}`} className="w-full h-full object-cover hover:opacity-90"/>
                      : <div className="w-full h-full grid place-items-center"><Loader2 className="w-3 h-3 animate-spin"/></div>}
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-xl border-2 border-foreground/10 bg-foreground/[0.02] p-4">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/70 mb-2">Admin override</div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { v: "force_resolve", l: "Force resolve",  i: ShieldCheck, tone: "emerald-700" },
                { v: "force_reverse", l: "Force reverse",  i: RotateCcw,   tone: "amber-800" },
                { v: "void",          l: "Void",           i: Ban,         tone: "rose-700" },
              ].map(o => (
                <button key={o.v} onClick={() => setAction(o.v)}
                  data-testid={`admin-claim-action-${o.v}`}
                  className={`rounded-xl border p-2 text-left text-[12px] transition-colors ${
                    action === o.v ? "bg-foreground text-background border-foreground" : "bg-white border-border hover:bg-secondary"
                  }`}>
                  <o.i className="w-3.5 h-3.5 mb-1"/>
                  <div className="font-medium">{o.l}</div>
                </button>
              ))}
            </div>
            {action === "force_resolve" && (
              <div className="mt-3">
                <Label>Deduction override · optional</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/55">$</span>
                  <Input value={override} onChange={e => setOverride(e.target.value)}
                    type="number" step="0.01"
                    placeholder={`Default ${money(c.deduction)}`}
                    className="pl-7"/>
                </div>
              </div>
            )}
            {action && (
              <>
                <div className="mt-3">
                  <Label>Internal note · audit trail</Label>
                  <Textarea value={note} onChange={e => setNote(e.target.value)}
                    rows={2} placeholder="Why is admin overriding?"/>
                </div>
                <Button onClick={submit} disabled={submitting}
                  data-testid="admin-claim-submit-override"
                  className="mt-3 w-full bg-foreground text-background">
                  {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
                  Apply {action.replace("_", " ")}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
function Field({ l, v }) {
  return <div className="rounded-lg border border-border bg-white px-3 py-2">
    <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">{l}</div>
    <div className="text-[12.5px] font-medium mt-0.5 capitalize">{v ?? "—"}</div>
  </div>;
}

/* ─────────────── 2 · Returns ─────────────── */
function ReturnsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const load = () => { setLoading(true);
    api.get("/admin/dropship/returns?limit=500")
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      {loading ? <Loading/> : rows.length === 0 ? <Empty what="returns"/> : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <div className="grid grid-cols-[100px_1fr_1fr_90px_100px_140px_36px] gap-3 px-4 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
            <div>Order</div><div>Buyer</div><div>Seller</div>
            <div className="text-right">Amount</div><div>Status</div><div>Filed</div><div></div>
          </div>
          <div className="divide-y divide-border max-h-[640px] overflow-y-auto">
            {rows.map(r => (
              <button key={r.id} onClick={() => setDrawer(r)}
                data-testid={`admin-return-row-${r.id}`}
                className="w-full grid grid-cols-[100px_1fr_1fr_90px_100px_140px_36px] gap-3 px-4 py-2.5 items-center text-[13px] hover:bg-secondary/30 text-left">
                <div className="mono text-[12px]">{r.order_code}</div>
                <div className="truncate">{r.buyer?.company || r.buyer?.name}</div>
                <div className="truncate">{r.seller?.company || r.seller?.name}</div>
                <div className="text-right font-medium">{money(r.amount)}</div>
                <div><Pill tone={statusTone(r.status)}>{r.status}</Pill></div>
                <div className="text-[11px] text-foreground/55">{formatTs(r.filed_at)}</div>
                <ChevronRight className="w-3.5 h-3.5 text-foreground/40"/>
              </button>
            ))}
          </div>
        </div>
      )}
      <ReturnDrawer ret={drawer} onClose={() => setDrawer(null)} onActioned={load}/>
    </div>
  );
}
function ReturnDrawer({ ret, onClose, onActioned }) {
  const [full, setFull] = useState(null);
  const [action, setAction] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (!ret?.id) return;
    api.get(`/admin/dropship/returns/${ret.id}`).then(r => setFull(r.data)).catch(() => {});
  }, [ret?.id]);
  if (!ret) return null;
  const r = full || ret;
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.patch(`/admin/dropship/returns/${ret.id}`, { action, note: note || undefined });
      toast.success("Return updated"); onActioned?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={!!ret} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Return · {r.order_code}</DialogTitle>
          <DialogDescription>Mutual return — admin can force-verify or force-reject.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-[13px]">
          <div className="grid grid-cols-2 gap-3">
            <Field l="Buyer" v={r.buyer?.company || r.buyer?.name}/>
            <Field l="Seller" v={r.seller?.company || r.seller?.name}/>
            <Field l="Amount" v={money(r.amount)}/>
            <Field l="Status" v={r.status}/>
          </div>
          <div className="rounded-xl border-2 border-foreground/10 bg-foreground/[0.02] p-4">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/70 mb-2">Admin override</div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { v: "force_verify", l: "Force verify", i: Check,    tone: "emerald" },
                { v: "force_reject", l: "Force reject", i: ShieldOff, tone: "rose" },
                { v: "void",         l: "Void",         i: Ban,       tone: "neutral" },
              ].map(o => (
                <button key={o.v} onClick={() => setAction(o.v)}
                  data-testid={`admin-return-action-${o.v}`}
                  className={`rounded-xl border p-2 text-left text-[12px] ${
                    action === o.v ? "bg-foreground text-background" : "bg-white border-border hover:bg-secondary"
                  }`}>
                  <o.i className="w-3.5 h-3.5 mb-1"/>
                  <div className="font-medium">{o.l}</div>
                </button>
              ))}
            </div>
            {action && (
              <>
                <div className="mt-3">
                  <Label>Note</Label>
                  <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2}/>
                </div>
                <Button onClick={submit} disabled={submitting}
                  data-testid="admin-return-submit-override"
                  className="mt-3 w-full bg-foreground text-background">
                  Apply {action.replace("_", " ")}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────── 3 · Sync rules ─────────────── */
function SyncRulesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true);
    api.get("/admin/dropship/sync-rules")
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const run = async (id) => {
    try {
      const { data } = await api.post(`/admin/dropship/sync-rules/${id}/run`);
      toast.success(`Rule ran · matched ${data.matched} · applied ${data.applied}`);
      load();
    } catch { toast.error("Run failed"); }
  };
  if (loading) return <Loading/>;
  if (!rows.length) return <Empty what="sync rules"/>;
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden">
      <div className="grid grid-cols-[1fr_120px_120px_90px_90px_90px] gap-3 px-4 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
        <div>Rule · Owner</div><div>Role</div><div>Last run</div>
        <div className="text-right">Match</div><div className="text-right">Apply</div><div></div>
      </div>
      <div className="divide-y divide-border">
        {rows.map(r => (
          <div key={r.id} data-testid={`admin-rule-row-${r.id}`}
            className="grid grid-cols-[1fr_120px_120px_90px_90px_90px] gap-3 px-4 py-2.5 items-center text-[13px] hover:bg-secondary/30">
            <div>
              <div className="font-medium truncate">{r.name}</div>
              <div className="text-[11px] text-foreground/55 truncate">{r.user?.company || r.user?.email}</div>
            </div>
            <div><Pill tone={r.role === "seller" ? "indigo" : "blue"}>{r.role}</Pill></div>
            <div className="text-[11px] text-foreground/55">{formatTs(r.last_run_at)}</div>
            <div className="text-right">{r.last_match_count || 0}</div>
            <div className="text-right">{r.last_apply_count || 0}</div>
            <button onClick={() => run(r.id)} className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/65 hover:text-foreground inline-flex items-center gap-1">
              <RefreshCw className="w-3 h-3"/>Run
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────── 4 · Shopify stores ─────────────── */
function ShopifyTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true);
    api.get("/admin/dropship/shopify-stores")
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const disconnect = async (id) => {
    if (!confirm("Force-disconnect this Shopify store? Token will be revoked.")) return;
    try {
      await api.delete(`/admin/dropship/shopify-stores/${id}`);
      toast.success("Store disconnected"); load();
    } catch { toast.error("Failed"); }
  };
  if (loading) return <Loading/>;
  if (!rows.length) return <Empty what="connected Shopify stores"/>;
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden">
      <div className="grid grid-cols-[1fr_1fr_140px_120px_100px_90px] gap-3 px-4 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
        <div>Store · User</div><div>Connection</div><div>Connected</div><div>Status</div><div>Token</div><div></div>
      </div>
      <div className="divide-y divide-border">
        {rows.map(r => (
          <div key={r.id} data-testid={`admin-store-row-${r.id}`}
            className="grid grid-cols-[1fr_1fr_140px_120px_100px_90px] gap-3 px-4 py-2.5 items-center text-[13px] hover:bg-secondary/30">
            <div className="min-w-0">
              <div className="font-medium truncate">{r.display_name}</div>
              <div className="text-[11px] mono text-foreground/55 truncate">{r.store_url}</div>
            </div>
            <div className="min-w-0">
              <div className="truncate">{r.user?.company || r.user?.email}</div>
              <div className="text-[10.5px] mono text-foreground/55 capitalize">{r.user?.role}</div>
            </div>
            <div className="text-[11px] text-foreground/55">{formatTs(r.shopify_oauth_at || r.created_at)}</div>
            <div><Pill tone={statusTone(r.status)}>{r.status || "—"}</Pill>
              {r.shopify_mock && <Pill tone="warn">mock</Pill>}
            </div>
            <div>{r.has_access_token ? <Pill tone="emerald">live</Pill> : <Pill tone="rose">missing</Pill>}</div>
            <button onClick={() => disconnect(r.id)} disabled={r.status === "disconnected"}
              className="text-[11px] mono uppercase tracking-[0.14em] text-rose-700 hover:text-rose-900 disabled:opacity-30 inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3"/>Disconnect
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────── 5 · Policies ─────────────── */
function PoliciesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/dropship/policies")
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Loading/>;
  if (!rows.length) return <Empty what="bilateral policies"/>;
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden">
      <div className="grid grid-cols-[1fr_1fr_140px_140px_120px] gap-3 px-4 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
        <div>Buyer</div><div>Seller</div><div>Payment policy</div><div>Shipping</div><div>SLA liability</div>
      </div>
      <div className="divide-y divide-border">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_140px_140px_120px] gap-3 px-4 py-2.5 items-center text-[13px] hover:bg-secondary/30">
            <div className="truncate">{r.buyer?.company || r.buyer?.name}</div>
            <div className="truncate">{r.seller?.company || r.seller?.name}</div>
            <div className="text-[11.5px] capitalize">{(r.payment_policy || "manual").replace("_", " ")}</div>
            <div className="text-[11.5px] capitalize">{(r.shipping_paid_by || "—").replace("_", " ")}</div>
            <div><Pill tone="indigo">{r.shipping_sla_liability || "—"}</Pill></div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────── 6 · CSV batches ─────────────── */
function CsvBatchesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/dropship/csv-batches")
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Loading/>;
  if (!rows.length) return <Empty what="CSV upload batches"/>;
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden">
      <div className="grid grid-cols-[1fr_1fr_120px_140px] gap-3 px-4 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
        <div>Buyer</div><div>Filename · Batch</div><div className="text-right">Rows</div><div>First seen</div>
      </div>
      <div className="divide-y divide-border max-h-[640px] overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_120px_140px] gap-3 px-4 py-2.5 items-center text-[13px] hover:bg-secondary/30">
            <div className="truncate">{r.buyer?.company || r.buyer?.name || r.buyer_id?.slice(0, 8)}</div>
            <div className="min-w-0">
              <div className="truncate">{r.filename || "—"}</div>
              <div className="text-[10.5px] mono text-foreground/55 truncate">{r.batch_id || ""}</div>
            </div>
            <div className="text-right font-medium">{r.rows}</div>
            <div className="text-[11px] text-foreground/55">{formatTs(r.first_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────── 7 · Onboarding ─────────────── */
function OnboardingTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/dropship/onboarding-progress")
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Loading/>;
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden">
      <div className="grid grid-cols-[1fr_140px_140px_140px_120px] gap-3 px-4 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
        <div>Buyer</div><div>Shopify</div><div>Products</div><div>Terms signed</div><div className="text-right">Done</div>
      </div>
      <div className="divide-y divide-border max-h-[640px] overflow-y-auto">
        {rows.map(r => {
          const tone = r.completion_pct === 100 ? "emerald" : r.completion_pct >= 50 ? "warn" : "rose";
          const Tick = ({ on }) => on ? <Check className="w-3.5 h-3.5 text-emerald-700"/> : <X className="w-3.5 h-3.5 text-rose-500"/>;
          return (
            <div key={r.id} className="grid grid-cols-[1fr_140px_140px_140px_120px] gap-3 px-4 py-2.5 items-center text-[13px] hover:bg-secondary/30">
              <div className="truncate">
                <div className="font-medium truncate">{r.company || r.name}</div>
                <div className="text-[11px] text-foreground/55 truncate">{r.email}</div>
              </div>
              <div><Tick on={r.steps?.shopify_connected}/></div>
              <div><Tick on={r.steps?.products_enabled}/></div>
              <div><Tick on={r.steps?.terms_signed}/></div>
              <div className="text-right"><Pill tone={tone}>{r.completion_pct}%</Pill></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



/* ─────────────── iter-87 shared mini-kit ─────────────── */
function Kpi({ label, value, icon: Icon, tone }) {
  const tones = { emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-700", indigo: "text-indigo-700" };
  return (
    <div className="rounded-2xl bg-white border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/40"/>}
      </div>
      <div className={`display text-2xl mt-1.5 ${tones[tone] || ""}`}>{value}</div>
    </div>
  );
}

function FilterBar({ q, setQ, onSearch, placeholder, sinceDays, setSinceDays, extra, counts, total, hideSearch }) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
      {!hideSearch && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
          <Input data-testid="adm-drop-filter-q" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
            placeholder={placeholder} className="h-9 pl-8 text-[13px] w-[260px]"/>
        </div>
      )}
      {extra}
      {setSinceDays && (
        <select data-testid="adm-drop-filter-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="180">Last 6 months</option>
          <option value="365">Last 1 year</option>
        </select>
      )}
      <button data-testid="adm-drop-filter-refresh" onClick={onSearch}
        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
        <RefreshCw size={13}/> Refresh
      </button>
      <div className="ml-auto flex items-center gap-3 text-[11px] text-foreground/55">
        <span>{total ?? 0} rows</span>
        {counts && Object.keys(counts).length > 0 && (
          <span className="flex items-center gap-1.5">
            {Object.entries(counts).slice(0, 4).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-secondary/60 border border-border">
                <span className="capitalize">{(k || "").replace(/_/g, " ")}</span><span className="font-medium">{v}</span>
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

function DataTable({ loading, rows, columns, empty, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid={`${testid}-table`}>
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
          <tr>{columns.map((c, i) => <th key={i} className="px-4 py-2.5 font-medium">{c.l}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55" data-testid={`${testid}-empty`}>{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.id || `${i}`} data-testid={`${testid}-row-${i}`} className="hover:bg-secondary/20">
              {columns.map((c, j) => <td key={j} className="px-4 py-3">{c.r(r, i)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stack({ top, sub }) {
  return (
    <div className="leading-tight">
      <div className="text-[13px] font-medium truncate max-w-[200px]">{top || "—"}</div>
      <div className="text-[11px] text-foreground/55 truncate max-w-[200px]">{sub || ""}</div>
    </div>
  );
}

/* ─────────────── 8 · Orders (iter-87 · P0) ─────────────── */
function OrdersTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (source) p.set("source", source);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/dropship/orders?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays, status, source]);

  return (
    <div className="space-y-4" data-testid="adm-drop-orders">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total orders"   value={data?.kpis?.total_count ?? 0}     icon={ShoppingCart}/>
        <Kpi label="Total GMV"      value={money(data?.kpis?.total_gmv ?? 0)} icon={BarChart3} tone="emerald"/>
        <Kpi label="Forwarded"      value={data?.kpis?.forwarded_count ?? 0} icon={Send}/>
        <Kpi label="Window"         value={`${sinceDays}d`} icon={Clock}/>
      </div>
      <FilterBar q={q} setQ={setQ} onSearch={load}
        placeholder="Code, customer, tracking…"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="adm-drop-orders-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="forwarded">Forwarded</option>
              <option value="shipped">Shipped</option>
              <option value="in_transit">In transit</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select data-testid="adm-drop-orders-source" value={source} onChange={(e) => setSource(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All sources</option>
              <option value="shopify_webhook">Shopify webhook</option>
              <option value="csv_upload">CSV upload</option>
              <option value="manual">Manual</option>
            </select>
          </>
        }
        counts={data?.by_status} total={data?.total}/>
      <DataTable loading={loading} rows={data?.rows || []} testid="adm-drop-orders"
        columns={[
          { l: "Order",   r: (x) => <span className="mono text-[11px]">{x.code || x.id?.slice(0, 8)}</span> },
          { l: "Buyer",   r: (x) => <Stack top={x.buyer?.company || x.buyer?.email} sub={x.buyer?.email}/> },
          { l: "Seller",  r: (x) => <Stack top={x.seller?.company || x.seller?.email} sub={x.seller?.email}/> },
          { l: "Customer", r: (x) => <Stack top={x.customer_name} sub={x.customer_email}/> },
          { l: "Source",  r: (x) => <span className="text-[10.5px] mono uppercase text-foreground/55">{(x.source || "—").replace(/_/g, " ")}</span> },
          { l: "Status",  r: (x) => <Pill tone={statusTone(x.status)}>{x.status}</Pill> },
          { l: "Track",   r: (x) => <span className="text-[11px] mono">{x.tracking_event_count ? `${x.tracking_event_count} evt` : "—"}</span> },
          { l: "Total",   r: (x) => <span className="display tabular-nums">{money(x.total)}</span> },
          { l: "Created", r: (x) => <span className="text-[11px] text-foreground/55">{formatTs(x.created_at).slice(0, 10)}</span> },
        ]}
        empty="No dropship orders in this window."/>
    </div>
  );
}

/* ─────────────── 9 · Invoices (iter-87 · P0) ─────────────── */
function InvoicesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sinceDays, setSinceDays] = useState(180);
  const [resendOpen, setResendOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/dropship/invoices?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load invoices"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays, status]);

  const downloadInvoice = async (inv, kind) => {
    try {
      const r = await api.get(`/dropship/invoices/${inv.id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.code || inv.id.slice(0, 8)}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-4" data-testid="adm-drop-invoices">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Invoices"        value={data?.kpis?.total_count ?? 0}            icon={Receipt}/>
        <Kpi label="Paid total"      value={money(data?.kpis?.paid_total_usd ?? 0)}   icon={Check} tone="emerald"/>
        <Kpi label="Outstanding"     value={money(data?.kpis?.outstanding_total_usd ?? 0)} icon={Clock} tone="amber"/>
        <Kpi label="Overdue count"   value={data?.kpis?.overdue_count ?? 0}           icon={AlertTriangle} tone="rose"/>
      </div>
      <FilterBar q={q} setQ={setQ} onSearch={load}
        placeholder="Code, buyer or seller…"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="adm-drop-inv-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="voided">Voided</option>
          </select>
        }
        counts={data?.by_status} total={data?.total}/>
      <DataTable loading={loading} rows={data?.rows || []} testid="adm-drop-inv"
        columns={[
          { l: "Code",   r: (x) => <span className="mono text-[11px]">{x.code || x.id?.slice(0, 8)}</span> },
          { l: "Buyer",  r: (x) => <Stack top={x.buyer?.company || x.buyer?.email} sub={x.buyer?.email}/> },
          { l: "Seller", r: (x) => <Stack top={x.seller?.company || x.seller?.email} sub={x.seller?.email}/> },
          { l: "Total",  r: (x) => <span className="display tabular-nums">{money(x.total)}</span> },
          { l: "Status", r: (x) => <Pill tone={statusTone(x.status)}>{x.status}</Pill> },
          { l: "Issued", r: (x) => <span className="text-[11px] text-foreground/55">{formatTs(x.issued_at || x.date).slice(0, 10)}</span> },
          { l: "Actions", r: (x) => (
            <div className="flex items-center gap-1.5">
              <button data-testid={`adm-drop-inv-pdf-${x.id}`} onClick={() => downloadInvoice(x, "pdf")}
                className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/65 hover:text-foreground inline-flex items-center gap-1"
                title="Download PDF (admin bypass)">
                <Download className="w-3 h-3"/>PDF
              </button>
              <button data-testid={`adm-drop-inv-csv-${x.id}`} onClick={() => downloadInvoice(x, "csv")}
                className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/65 hover:text-foreground inline-flex items-center gap-1"
                title="Download CSV (admin bypass)">
                <Download className="w-3 h-3"/>CSV
              </button>
              <button data-testid={`adm-drop-inv-email-${x.id}`} onClick={() => setResendOpen(x)}
                className="text-[10.5px] mono uppercase tracking-[0.14em] text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1"
                title="Re-send email on behalf of seller">
                <Mail className="w-3 h-3"/>Resend
              </button>
            </div>
          )},
        ]}
        empty="No dropship invoices in this window."/>
      <ResendEmailDialog inv={resendOpen} onClose={() => setResendOpen(null)} onSent={load}/>
    </div>
  );
}

function ResendEmailDialog({ inv, onClose, onSent }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { setReason(""); }, [inv?.id]);
  if (!inv) return null;
  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Reason ≥3 chars"); return; }
    setSubmitting(true);
    try {
      await api.post(`/admin/dropship/invoices/${inv.id}/resend-email`, { reason: reason.trim() });
      toast.success(`Email re-sent to ${inv.buyer?.email}`);
      onSent?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={!!inv} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Re-send invoice email · {inv.code}</DialogTitle>
          <DialogDescription>
            Sends a fresh copy of this dropship invoice to {inv.buyer?.email || "buyer"} on behalf of {inv.seller?.company || inv.seller?.email}. Audit-chained.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Reason · audit trail (≥3 chars)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              data-testid="adm-drop-inv-resend-reason"
              placeholder="Why is admin re-sending this invoice?"/>
          </div>
          <Button onClick={submit} disabled={submitting || reason.trim().length < 3}
            data-testid="adm-drop-inv-resend-submit"
            className="w-full bg-foreground text-background">
            {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            Send email now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────── 10 · Products (iter-87 · P0) ─────────────── */
function ProductsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fulfillment, setFulfillment] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: "100" });
      if (fulfillment) p.set("fulfillment", fulfillment);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/dropship/products?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load products"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fulfillment]);

  return (
    <div className="space-y-4" data-testid="adm-drop-products">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Total products"           value={data?.kpis?.total_products ?? 0} icon={Package}/>
        <Kpi label="Buyer enable rows"        value={data?.kpis?.enabled_buyers_total ?? 0} icon={ShoppingCart}/>
        <Kpi label="Distinct enabled products" value={data?.kpis?.distinct_enabled_products ?? 0} icon={Check} tone="emerald"/>
      </div>
      <FilterBar q={q} setQ={setQ} onSearch={load} placeholder="Name or SKU…"
        extra={
          <select data-testid="adm-drop-prod-fulfillment" value={fulfillment} onChange={(e) => setFulfillment(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All fulfillment</option>
            <option value="dropship">Dropship</option>
            <option value="wholesale">Wholesale</option>
          </select>
        }
        counts={data?.by_fulfillment} total={data?.total}/>
      <DataTable loading={loading} rows={data?.rows || []} testid="adm-drop-prod"
        columns={[
          { l: "Product", r: (x) => <Stack top={x.name} sub={x.sku || x.id?.slice(0, 8)}/> },
          { l: "Seller",  r: (x) => <Stack top={x.seller?.company || x.seller?.email} sub={x.seller?.email}/> },
          { l: "Wholesale", r: (x) => <span className="display tabular-nums">{money(x.price)}</span> },
          { l: "Fulfillment", r: (x) => <Pill tone={x.fulfillment === "dropship" ? "indigo" : "neutral"}>{x.fulfillment || "—"}</Pill> },
          { l: "Source",  r: (x) => <span className="text-[10.5px] mono text-foreground/55 uppercase">{x.dropship_source || "—"}</span> },
          { l: "Buyers", r: (x) => (
            <span className={`chip border text-[10.5px] ${(x.dropship_buyer_count || 0) > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
              {x.dropship_buyer_count || 0}
            </span>
          )},
          { l: "Retail span", r: (x) => (x.dropship_buyer_count > 0
              ? <span className="text-[11px] mono">{money(x.retail_min)} – {money(x.retail_max)}</span>
              : <span className="text-[11px] text-foreground/40">—</span>) },
          { l: "MAP Δ", r: (x) => (x.map_spread_pct > 0
              ? <span className={`chip border text-[10.5px] ${x.map_spread_pct > 25 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{x.map_spread_pct}%</span>
              : <span className="text-[11px] text-foreground/40">—</span>) },
        ]}
        empty="No products found."/>
    </div>
  );
}

/* ─────────────── 11 · Tracking events (iter-87 · P1) ─────────────── */
function TrackingTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [sinceDays, setSinceDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      const r = await api.get(`/admin/dropship/tracking-events?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load tracking events"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays, status]);

  return (
    <div className="space-y-4" data-testid="adm-drop-tracking">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Total events" value={data?.kpis?.total_events ?? 0} icon={Truck}/>
        <Kpi label="Window"       value={`${sinceDays}d`} icon={Clock}/>
        <Kpi label="Distinct statuses" value={Object.keys(data?.by_status || {}).length} icon={ClipboardList}/>
      </div>
      <FilterBar q="" setQ={() => {}} hideSearch onSearch={load}
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="adm-drop-track-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All statuses</option>
            <option value="label_created">Label created</option>
            <option value="picked_up">Picked up</option>
            <option value="in_transit">In transit</option>
            <option value="out_for_delivery">Out for delivery</option>
            <option value="delivered">Delivered</option>
            <option value="exception">Exception</option>
          </select>
        }
        counts={data?.by_status} total={data?.total}/>
      <DataTable loading={loading} rows={data?.rows || []} testid="adm-drop-track"
        columns={[
          { l: "Order",   r: (x) => <span className="mono text-[11px]">{x.order_code || x.order_id?.slice(0, 8)}</span> },
          { l: "Carrier", r: (x) => <span className="text-[11px]">{x.carrier || "—"}</span> },
          { l: "Tracking #", r: (x) => <span className="mono text-[11px]">{x.tracking_number || "—"}</span> },
          { l: "Buyer",   r: (x) => <Stack top={x.buyer?.company || x.buyer?.email} sub={x.buyer?.email}/> },
          { l: "Seller",  r: (x) => <Stack top={x.seller?.company || x.seller?.email} sub={x.seller?.email}/> },
          { l: "Status",  r: (x) => <Pill tone={statusTone(x.event_status)}>{x.event_status}</Pill> },
          { l: "Note",    r: (x) => <span className="text-[11px] text-foreground/55 truncate max-w-[200px]">{x.event_note || "—"}</span> },
          { l: "At",      r: (x) => <span className="text-[11px] text-foreground/55">{formatTs(x.event_at)}</span> },
        ]}
        empty="No tracking events in this window."/>
    </div>
  );
}

/* ─────────────── 12 · SLA-extensions (iter-87 · P1) ─────────────── */
function SlaTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (action) p.set("action", action);
      const r = await api.get(`/admin/dropship/sla-extensions?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load SLA extensions"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays, action]);

  return (
    <div className="space-y-4" data-testid="adm-drop-sla">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Total requests"   value={data?.kpis?.total_requests ?? 0} icon={ClipboardList}/>
        <Kpi label="Window"           value={`${sinceDays}d`} icon={Clock}/>
        <Kpi label="Distinct outcomes" value={Object.keys(data?.by_action || {}).length} icon={Filter}/>
      </div>
      <FilterBar q="" setQ={() => {}} hideSearch onSearch={load}
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="adm-drop-sla-action" value={action} onChange={(e) => setAction(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All outcomes</option>
            <option value="requested">Requested</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="auto_accepted">Auto accepted</option>
          </select>
        }
        counts={data?.by_action} total={data?.total}/>
      {data?.top_sellers && data.top_sellers.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="adm-drop-sla-top">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">
            Top sellers by extension count · performance signal
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {data.top_sellers.slice(0, 8).map((s, i) => (
              <div key={s.seller_id || i} data-testid={`adm-drop-sla-top-${i}`}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-secondary/40 border border-border">
                <span className="text-[10px] mono text-foreground/55">#{i + 1}</span>
                <div className="flex-1 min-w-0 leading-tight">
                  <div className="text-[12px] font-medium truncate">{s.company || s.email || "—"}</div>
                  <div className="text-[10.5px] text-foreground/55 truncate">{s.email || ""}</div>
                </div>
                <Pill tone={s.extension_count >= 10 ? "rose" : s.extension_count >= 5 ? "warn" : "neutral"}>
                  {s.extension_count} ext
                </Pill>
              </div>
            ))}
          </div>
        </div>
      )}
      <DataTable loading={loading} rows={data?.rows || []} testid="adm-drop-sla"
        columns={[
          { l: "Order",   r: (x) => <span className="mono text-[11px]">{x.order_code || x.order_id?.slice(0, 8)}</span> },
          { l: "Seller",  r: (x) => <Stack top={x.seller?.company || x.seller?.email} sub={x.seller?.email}/> },
          { l: "Buyer",   r: (x) => <Stack top={x.buyer?.company || x.buyer?.email} sub={x.buyer?.email}/> },
          { l: "Days",    r: (x) => <span className="mono text-[11px]">+{x.requested_days || "?"}d</span> },
          { l: "Reason",  r: (x) => <span className="text-[11px] text-foreground/55 truncate max-w-[200px]">{x.reason || "—"}</span> },
          { l: "Outcome", r: (x) => <Pill tone={statusTone(x.ext_status)}>{x.ext_status}</Pill> },
          { l: "N",       r: (x) => <span className="text-[11px] mono">{x.extension_n || 1}/3</span> },
          { l: "At",      r: (x) => <span className="text-[11px] text-foreground/55">{formatTs(x.requested_at)}</span> },
        ]}
        empty="No SLA extension requests in this window."/>
    </div>
  );
}

/* ─────────────── 13 · Analytics (iter-87 · P2) ─────────────── */
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/dropship/analytics?since_days=${sinceDays}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays]);

  const orders = data?.orders || {};
  const invs = data?.invoices || {};
  const issues = data?.open_issues || {};
  if (loading) return <Loading/>;
  if (!data) return <Empty what="analytics data"/>;

  return (
    <div className="space-y-4" data-testid="adm-drop-analytics">
      <div className="flex items-center gap-2 bg-white border border-border rounded-xl p-3">
        <span className="text-[12.5px] text-foreground/65">Window</span>
        <select data-testid="adm-drop-analytics-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="180">Last 6 months</option>
          <option value="365">Last 1 year</option>
        </select>
        <button data-testid="adm-drop-analytics-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
        <div className="ml-auto text-[10.5px] mono text-foreground/45">As of {formatTs(data.as_of)}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Orders"          value={orders.total_count ?? 0}        icon={ShoppingCart}/>
        <Kpi label="GMV"             value={money(orders.total_gmv ?? 0)}   icon={BarChart3} tone="emerald"/>
        <Kpi label="Fulfillment"     value={`${orders.fulfillment_rate_pct ?? 0}%`} icon={Truck}/>
        <Kpi label="Ship rate"       value={`${orders.ship_rate_pct ?? 0}%`} icon={Send} tone="indigo"/>
        <Kpi label="Invoices billed" value={money(invs.total_billed_usd ?? 0)} icon={Receipt}/>
        <Kpi label="Claims open"     value={issues.claims_open ?? 0}        icon={AlertTriangle} tone="amber"/>
        <Kpi label="Returns open"    value={issues.returns_open ?? 0}       icon={RotateCcw} tone="amber"/>
        <Kpi label="Delivered"       value={orders.delivered ?? 0}          icon={Check} tone="emerald"/>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-white border border-border rounded-xl p-3" data-testid="adm-drop-analytics-top-sellers">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">Top 10 sellers by GMV</div>
          <div className="space-y-1.5">
            {(data.top_sellers || []).map((s, i) => (
              <div key={s.seller_id || i} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-secondary/40 border border-border">
                <span className="text-[10px] mono text-foreground/55">#{i + 1}</span>
                <div className="flex-1 min-w-0 leading-tight">
                  <div className="text-[12px] font-medium truncate">{s.company || s.email}</div>
                  <div className="text-[10.5px] text-foreground/55 truncate">{s.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="display text-[12.5px] tabular-nums">{money(s.gmv)}</div>
                  <div className="text-[10px] text-foreground/55">{s.order_count} orders</div>
                </div>
              </div>
            ))}
            {(data.top_sellers || []).length === 0 && <div className="text-[11px] text-foreground/55 text-center py-4">No sellers yet.</div>}
          </div>
        </div>
        <div className="bg-white border border-border rounded-xl p-3" data-testid="adm-drop-analytics-top-buyers">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">Top 10 buyers by GMV</div>
          <div className="space-y-1.5">
            {(data.top_buyers || []).map((b, i) => (
              <div key={b.buyer_id || i} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-secondary/40 border border-border">
                <span className="text-[10px] mono text-foreground/55">#{i + 1}</span>
                <div className="flex-1 min-w-0 leading-tight">
                  <div className="text-[12px] font-medium truncate">{b.company || b.email}</div>
                  <div className="text-[10.5px] text-foreground/55 truncate">{b.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="display text-[12.5px] tabular-nums">{money(b.gmv)}</div>
                  <div className="text-[10px] text-foreground/55">{b.order_count} orders</div>
                </div>
              </div>
            ))}
            {(data.top_buyers || []).length === 0 && <div className="text-[11px] text-foreground/55 text-center py-4">No buyers yet.</div>}
          </div>
        </div>
      </div>
      {data.daily_volume && data.daily_volume.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="adm-drop-analytics-daily">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">Daily volume · {data.daily_volume.length} days</div>
          <div className="overflow-x-auto">
            <div className="flex items-end gap-1 h-32 min-w-full">
              {data.daily_volume.map((d) => {
                const max = Math.max(...data.daily_volume.map((x) => x.count));
                const h = max > 0 ? (d.count / max) * 100 : 0;
                return (
                  <div key={d.date} className="flex-1 min-w-[10px] flex flex-col items-center gap-1" title={`${d.date}: ${d.count} orders · ${money(d.gmv)}`}>
                    <div className="w-full bg-foreground/80 rounded-t" style={{ height: `${h}%` }}/>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[9px] mono text-foreground/45 mt-1">
              <span>{data.daily_volume[0]?.date}</span>
              <span>{data.daily_volume[data.daily_volume.length - 1]?.date}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
